import {
  BelowFoldExperimentV2Data,
  CreatorOfficeHoursData,
  FooterAttachmentData,
  LargeProductSuitCardsData,
  SimplePlanOptionsSectionData,
  TestimonialsCarouselData,
  UsedBySectionData,
  VerticalsButtonsGroupData,
  WaysToUseCarouselData
} from './types'
import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { FAQsSlice } from '@app/common/src/components/FAQs/types'
import { FC, ReactNode, memo, useMemo } from 'react'
import { PrismicAccordionSlice } from '@dtx-company/true-common/src/types/PrismicAccordionSlice'
import dynamic from 'next/dynamic'

const Accordion = dynamic(() =>
  import('../../../UseCases/subcomponents/Accordion').then(mod => mod.Accordion)
)
const FAQs = dynamic(() => import('@app/common/src/components/FAQs/FAQs').then(mod => mod.FAQs))
const CreatorOfficeHoursSection = dynamic(() => import('./subcomponents/CreatorOfficeHoursSection'))
const FooterAttachmentSection = dynamic(() => import('./subcomponents/FooterAttachmentSection'))
const LargeProductSuitCardsSection = dynamic(
  () => import('./subcomponents/LargeProductSuitCardsSection')
)
const SimplePlanOptionsSection = dynamic(() => import('./subcomponents/SimplePlansOptionsSection'))
const TestimonialsCarousel = dynamic(() => import('./subcomponents/TestimonialsCarousel'))
const UsedBySection = dynamic(() => import('./subcomponents/UsedBySection'))
const VerticalsButtonsGroup = dynamic(() => import('./subcomponents/VerticalsButtonsGroup'))
const WaysToUseCarouselSection = dynamic(() => import('./subcomponents/WaysToUseCarouselSection'))

const convertHomepageBelowFoldDataToSection = (data: BelowFoldExperimentV2Data): ReactNode[] =>
  data.map((slice, i) => {
    switch (slice.slice_type) {
      case 'large_product_card':
        return (
          <LargeProductSuitCardsSection
            data={slice as LargeProductSuitCardsData}
            key={`${slice.slice_type}_${i}`}
            sectionId={slice.slice_type}
          />
        )
      case 'simple_plans_cards_columns':
        return (
          <SimplePlanOptionsSection
            data={slice as SimplePlanOptionsSectionData}
            key={`${slice.slice_type}_${i}`}
            sectionId={slice.slice_type}
          />
        )
      case 'testimonials_carousel':
        return (
          <TestimonialsCarousel
            data={slice as TestimonialsCarouselData}
            key={(slice as TestimonialsCarouselData).id}
            sectionId={slice.slice_type}
          />
        )
      case 'creators_office_hours_card':
        return (
          <CreatorOfficeHoursSection
            data={slice as CreatorOfficeHoursData}
            key={`${slice.slice_type}_${i}`}
            sectionId={slice.slice_type}
          />
        )
      case 'faqs':
        return (
          <FAQs
            id={(slice as FAQsSlice).id}
            slice_type={slice.slice_type}
            primary={(slice as FAQsSlice).primary}
            items={(slice as FAQsSlice).items}
            key={`${slice.slice_type}_${i}`}
          />
        )
      case 'footer_attachement':
        return (
          <FooterAttachmentSection
            data={slice as FooterAttachmentData}
            key={`${slice.slice_type}_${i}`}
            sectionId={slice.slice_type}
          />
        )
      case 'ways-to-use_carousel_1':
        return (
          <WaysToUseCarouselSection
            data={slice as WaysToUseCarouselData}
            key={`${slice.slice_type}_${i}`}
            sectionId={slice.slice_type}
          />
        )
      case 'buttons_group':
        return (
          <VerticalsButtonsGroup
            data={slice as VerticalsButtonsGroupData}
            key={`${slice.slice_type}_${i}`}
            sectionId={slice.slice_type}
          />
        )
      case 'contact_sales_page_used_by_footer':
        return (
          <UsedBySection
            data={slice as UsedBySectionData}
            key={`${slice.slice_type}_${i}`}
            dark={true}
            sectionId={slice.slice_type}
          />
        )
      case 'accordion':
        return (
          <Accordion
            key={`${slice.slice_type}_${i}`}
            primary={(slice as PrismicAccordionSlice).primary}
            items={(slice as PrismicAccordionSlice).items}
            slice_type={slice.slice_type}
            slice_label="accordion"
          />
        )
      default:
        return null
    }
  })

export const LongAndColorful: FC<{
  data: BelowFoldExperimentV2Data
}> = ({ data }) => {
  data.splice(data.length - 1, 0, { slice_type: 'faq' })

  const sections: ReactNode[] = useMemo(() => {
    return convertHomepageBelowFoldDataToSection(data)
  }, [data])

  return <Box display="block">{sections}</Box>
}

export default memo(LongAndColorful)
